/**
 * Stable per-install / per-browser device id.
 *
 * Used to tag outgoing voice-call signals so a device can ignore its own
 * echoed signals (now that call signals fan out to every device of both
 * parties) and detect when a *sibling* device of the same user answered or
 * declined a call. Also used to correlate live call telemetry per install
 * (see debug/call-flow-log.ts).
 *
 * `getDeviceId()` stays SYNCHRONOUS (localStorage) for the hot call-signal
 * path. On native we additionally mirror the id into Capacitor Preferences so
 * it survives a WebView cache clear / localStorage eviction (which would
 * otherwise mint a new id on next launch): {@link primeDeviceId} hydrates from
 * Preferences on startup and reconciles the two stores.
 */
import { Preferences } from '@capacitor/preferences'
import { isNativeApp } from './platform'

const STORAGE_KEY = 'ascend.deviceId'
const PREFS_KEY = 'ascend-device-id'

let cachedDeviceId: string | null = null
let priming: Promise<string> | null = null

function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function getDeviceId(): string {
  if (cachedDeviceId) return cachedDeviceId

  try {
    const existing = window.localStorage.getItem(STORAGE_KEY)
    if (existing && existing.length >= 8) {
      cachedDeviceId = existing
      return existing
    }
    const fresh = generateId()
    window.localStorage.setItem(STORAGE_KEY, fresh)
    cachedDeviceId = fresh
    return fresh
  } catch {
    // Private mode / storage unavailable — keep an in-memory id for the session.
    if (!cachedDeviceId) cachedDeviceId = generateId()
    return cachedDeviceId
  }
}

/**
 * Replace this install's device id with a fresh one (persisted to every store
 * the id lives in). Used when the server rejects the old id's secret and this
 * install holds none: it must register as a NEW install rather than re-claim
 * the old row.
 */
export async function rotateDeviceId(): Promise<string> {
  const fresh = generateId()
  cachedDeviceId = fresh
  priming = Promise.resolve(fresh)
  try {
    window.localStorage.setItem(STORAGE_KEY, fresh)
  } catch {
    /* localStorage may be unavailable; the in-memory id still holds for this session */
  }
  if (isNativeApp()) {
    try {
      await Preferences.set({ key: PREFS_KEY, value: fresh })
    } catch {
      /* best effort; localStorage mirror above */
    }
  }
  return fresh
}

/** The resolved device id if known, else null (for hot paths that must not throw). */
export function getCachedDeviceId(): string | null {
  return cachedDeviceId
}

/**
 * Resolve the device id, hydrating from (and reconciling with) Capacitor
 * Preferences on native so it survives localStorage eviction. Returns the
 * stable id. Idempotent; concurrent calls share one resolution. Never throws.
 */
export async function primeDeviceId(): Promise<string> {
  if (priming) return priming
  priming = (async () => {
    if (!isNativeApp()) return getDeviceId()
    try {
      const { value } = await Preferences.get({ key: PREFS_KEY })
      if (value && value.length >= 8) {
        // Preferences is authoritative on native — mirror into localStorage +
        // the in-memory cache so getDeviceId() returns the persisted id.
        cachedDeviceId = value
        try {
          window.localStorage.setItem(STORAGE_KEY, value)
        } catch {
          /* localStorage may be unavailable */
        }
        return value
      }
      // No persisted id yet — take the localStorage/generated one and persist it.
      const id = getDeviceId()
      await Preferences.set({ key: PREFS_KEY, value: id })
      return id
    } catch {
      return getDeviceId()
    }
  })()
  try {
    return await priming
  } catch {
    return getDeviceId()
  }
}
